import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  MyTaskItem,
  MyTasksDueStateFilter,
  MyTasksFilterOption,
  MyTasksListResult,
  MyTasksStatusFilter,
  MyTasksSummary,
} from '../models/my-tasks.models';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class MyTasksApiService {
  private readonly http = inject(HttpClient);

  async getMyTasks(params: {
    page: number;
    pageSize: number;
    status: MyTasksStatusFilter;
    dueState: MyTasksDueStateFilter;
    workflowId: string | null;
    projectId: string | null;
    search: string;
  }): Promise<MyTasksListResult> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    if (params.status !== 'all') {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.dueState !== 'all') {
      httpParams = httpParams.set('dueState', params.dueState);
    }
    if (params.workflowId) {
      httpParams = httpParams.set('workflowId', params.workflowId);
    }
    if (params.projectId) {
      httpParams = httpParams.set('projectId', params.projectId);
    }
    if (params.search.trim()) {
      httpParams = httpParams.set('q', params.search.trim());
    }

    const response = await firstValueFrom(
      this.http.get<unknown>('/api/my-tasks', { params: httpParams })
    );
    return this.mapListResponse(response, params.page, params.pageSize);
  }

  async getSummary(params: {
    status: MyTasksStatusFilter;
    dueState: MyTasksDueStateFilter;
    workflowId: string | null;
    projectId: string | null;
    search: string;
  }): Promise<MyTasksSummary> {
    let httpParams = new HttpParams();
    if (params.status !== 'all') {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.dueState !== 'all') {
      httpParams = httpParams.set('dueState', params.dueState);
    }
    if (params.workflowId) {
      httpParams = httpParams.set('workflowId', params.workflowId);
    }
    if (params.projectId) {
      httpParams = httpParams.set('projectId', params.projectId);
    }
    if (params.search.trim()) {
      httpParams = httpParams.set('q', params.search.trim());
    }

    try {
      const response = await firstValueFrom(
        this.http.get<unknown>('/api/my-tasks/summary', { params: httpParams })
      );
      return this.mapSummary(response);
    } catch {
      return {
        openCount: 0,
        completedCount: 0,
        overdueCount: 0,
        dueSoonCount: 0,
      };
    }
  }

  private mapListResponse(raw: unknown, fallbackPage: number, fallbackPageSize: number): MyTasksListResult {
    const source = this.asRecord(raw);
    const itemsRaw = Array.isArray(raw)
      ? raw
      : Array.isArray(source['items'])
        ? source['items']
        : Array.isArray(source['data'])
          ? source['data']
          : [];

    const workflowsRaw = Array.isArray(source['workflows'])
      ? source['workflows']
      : Array.isArray(source['workflowOptions'])
        ? source['workflowOptions']
        : [];
    const projectsRaw = Array.isArray(source['projects'])
      ? source['projects']
      : Array.isArray(source['projectOptions'])
        ? source['projectOptions']
        : [];

    const embeddedSummary =
      source['summary'] && typeof source['summary'] === 'object'
        ? this.mapSummary(source['summary'])
        : null;

    return {
      items: itemsRaw.map((item) => this.mapItem(item)).filter((item) => !!item.cardId),
      page: this.asNumber(source['page'], fallbackPage),
      pageSize: this.asNumber(source['pageSize'], fallbackPageSize),
      totalCount: this.asNumber(
        source['totalCount'],
        this.asNumber(source['total'], this.asNumber(source['count']))
      ),
      summary: embeddedSummary,
      workflowOptions: workflowsRaw.map((item) => this.mapFilterOption(item)).filter((item) => !!item.id),
      projectOptions: projectsRaw.map((item) => this.mapFilterOption(item)).filter((item) => !!item.id),
    };
  }

  private mapItem(raw: unknown): MyTaskItem {
    const source = this.asRecord(raw);
    const cardId =
      this.asString(source['cardId']) ||
      this.asString(source['id']) ||
      this.asString(source['taskId']);
    const projectName = this.asString(source['projectName']) || this.asString(source['project']);
    const workflowName = this.asString(source['workflowName']) || this.asString(source['workflow']);
    const stageName =
      this.asString(source['stageName']) ||
      this.asString(source['statusName']) ||
      this.asString(source['stage']);
    const dueDate = this.asNullableString(source['dueDate']) || this.asNullableString(source['deadline']);
    const isCompleted =
      this.asBoolean(source['isCompleted']) ||
      this.asString(source['status']).toLowerCase() === 'completed';
    const isOverdue =
      this.asBoolean(source['isOverdue']) ||
      this.asString(source['dueState']).toLowerCase() === 'overdue';
    const isDueSoon =
      this.asBoolean(source['isDueSoon']) ||
      this.asString(source['dueState']).toLowerCase() === 'duesoon';

    return {
      id: this.asString(source['id']) || cardId,
      cardId,
      title: this.asString(source['title']) || this.asString(source['cardTitle']) || 'Card',
      projectId: this.asString(source['projectId']),
      projectName,
      workflowId: this.asString(source['workflowId']),
      workflowName,
      workflowColor: this.asNullableString(source['workflowColor']),
      stageName: stageName || '-',
      dueDate,
      updatedAt: this.asString(source['updatedAt']) || this.asString(source['lastUpdatedAt']),
      statusLabel:
        this.asString(source['statusLabel']) || (isCompleted ? 'completed' : 'open'),
      isCompleted,
      isOverdue,
      isDueSoon,
    };
  }

  private mapSummary(raw: unknown): MyTasksSummary {
    const source = this.asRecord(raw);
    return {
      openCount: this.asNumber(
        source['openCount'],
        this.asNumber(source['pendingCount'])
      ),
      completedCount: this.asNumber(source['completedCount']),
      overdueCount: this.asNumber(source['overdueCount']),
      dueSoonCount: this.asNumber(
        source['dueSoonCount'],
        this.asNumber(source['upcomingCount'])
      ),
    };
  }

  private mapFilterOption(raw: unknown): MyTasksFilterOption {
    const source = this.asRecord(raw);
    return {
      id: this.asString(source['id']) || this.asString(source['value']),
      label: this.asString(source['label']) || this.asString(source['name']),
    };
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    const parsed = this.asString(value).trim();
    return parsed ? parsed : null;
  }

  private asBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }

  private asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
  }
}
